import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import dayjs from "dayjs";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, FileText, Video, Edit2, Trash2, ExternalLink, Play } from "lucide-react";
import { toast } from "sonner";
import { getYoutubeThumbnail, getYoutubeEmbed } from "../lib/youtube";

export default function DietExercise({ category }) {
  const [blogs, setBlogs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [showAllBlogs, setShowAllBlogs] = useState(false);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoForm, setVideoForm] = useState({ title: "", youtube_url: "", description: "" });
  const [videoEditId, setVideoEditId] = useState(null);
  const [preview, setPreview] = useState(null);

  const label = category === "diet" ? "Diet" : "Exercise";

  const loadBlogs = (all = false) => {
    const params = { category };
    if (!all) params.limit = 4;
    api.get("/blogs", { params }).then((r) => setBlogs(r.data));
  };
  const loadVideos = (all = false) => {
    const params = { category };
    if (!all) params.limit = 4;
    api.get("/videos", { params }).then((r) => setVideos(r.data));
  };
  const load = () => {
    loadBlogs(showAllBlogs);
    loadVideos(showAllVideos);
  };
  useEffect(() => {
    setShowAllBlogs(false);
    setShowAllVideos(false);
    loadBlogs(false);
    loadVideos(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const submitVideo = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...videoForm, category };
      if (videoEditId) {
        await api.put(`/videos/${videoEditId}`, payload);
        toast.success("Video updated");
      } else {
        await api.post("/videos", payload);
        toast.success("Video added");
      }
      setVideoOpen(false);
      setVideoForm({ title: "", youtube_url: "", description: "" });
      setVideoEditId(null);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const openEditVideo = (v) => {
    setVideoForm({ title: v.title, youtube_url: v.youtube_url, description: v.description || "" });
    setVideoEditId(v.id);
    setVideoOpen(true);
  };

  const removeVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/videos/${id}`);
    load();
  };

  const removeBlog = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    await api.delete(`/blogs/${id}`);
    load();
  };

  return (
    <div className="space-y-6 fade-up">
      <header>
        <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">
          Wellness library
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">{label}</h1>
        <p className="text-muted-foreground mt-2">
          Blogs and videos curated for {label.toLowerCase()} guidance.
        </p>
      </header>

      <Tabs defaultValue="blogs">
        <TabsList>
          <TabsTrigger value="blogs" data-testid="tab-blogs">
            <FileText className="size-4 mr-2" />
            Blogs ({blogs.length})
          </TabsTrigger>
          <TabsTrigger value="videos" data-testid="tab-videos">
            <Video className="size-4 mr-2" />
            Videos ({videos.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="blogs" className="space-y-4 pt-4">
          <div className="flex justify-end">
            <Link to={`/blog/new?category=${category}`}>
              <Button data-testid={`add-${category}-blog-btn`}>
                <Plus className="size-4 mr-2" /> New Blog
              </Button>
            </Link>
          </div>
          {blogs.length === 0 ? (
            <Card className="p-10 text-center border-dashed">
              <FileText className="size-7 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No blogs yet. Write your first one.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {blogs.map((b) => (
                <Card key={b.id} className="p-5" data-testid={`blog-card-${b.id}`}>
                  {b.cover_image && (
                    <img
                      src={b.cover_image}
                      alt={b.title}
                      className="w-full h-40 object-cover rounded-md border border-border mb-3"
                    />
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="capitalize">{b.category}</Badge>
                    {b.status === "draft" && (
                      <Badge variant="secondary" className="capitalize">Draft</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {dayjs(b.created_at).format("MMM D, YYYY")}
                    </span>
                  </div>
                  <p className="font-heading text-lg font-semibold">{b.title}</p>
                  {b.excerpt && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{b.excerpt}</p>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Link to={`/blog/${b.id}`}>
                      <Button variant="outline" size="sm" data-testid={`edit-blog-${b.id}`}>
                        <Edit2 className="size-3.5 mr-1" /> Edit
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBlog(b.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`del-blog-${b.id}`}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
          {!showAllBlogs && blogs.length >= 4 && (
            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAllBlogs(true);
                  loadBlogs(true);
                }}
                data-testid={`view-all-${category}-blogs`}
              >
                View all blogs
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="videos" className="space-y-4 pt-4">
          <div className="flex justify-end">
            <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => {
                    setVideoForm({ title: "", youtube_url: "", description: "" });
                    setVideoEditId(null);
                  }}
                  data-testid={`add-${category}-video-btn`}
                >
                  <Plus className="size-4 mr-2" /> Add Video
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {videoEditId ? "Edit video" : "Add YouTube video"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={submitVideo} className="space-y-3">
                  <div>
                    <Label>Title *</Label>
                    <Input
                      required
                      value={videoForm.title}
                      onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                      data-testid="video-form-title"
                    />
                  </div>
                  <div>
                    <Label>YouTube URL *</Label>
                    <Input
                      required
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={videoForm.youtube_url}
                      onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })}
                      data-testid="video-form-url"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      rows={3}
                      value={videoForm.description}
                      onChange={(e) =>
                        setVideoForm({ ...videoForm, description: e.target.value })
                      }
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="video-form-submit">
                      {videoEditId ? "Update" : "Save"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {videos.length === 0 ? (
            <Card className="p-10 text-center border-dashed">
              <Video className="size-7 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No videos added yet.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((v) => (
                <Card
                  key={v.id}
                  className="overflow-hidden group"
                  data-testid={`video-card-${v.id}`}
                >
                  <button
                    type="button"
                    className="block relative w-full"
                    onClick={() => setPreview(v)}
                  >
                    <img
                      src={getYoutubeThumbnail(v.youtube_url)}
                      alt={v.title}
                      className="w-full aspect-video object-cover"
                    />
                    <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/30 transition-colors">
                      <div className="size-12 rounded-full bg-white/95 grid place-items-center shadow">
                        <Play className="size-5 text-primary fill-primary" />
                      </div>
                    </div>
                  </button>
                  <div className="p-4">
                    <p className="font-heading font-semibold line-clamp-2">{v.title}</p>
                    {v.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {v.description}
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <a
                        href={v.youtube_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
                      >
                        Open <ExternalLink className="size-3" />
                      </a>
                      <div className="ml-auto flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditVideo(v)}
                          data-testid={`edit-video-${v.id}`}
                        >
                          <Edit2 className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVideo(v.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-video-${v.id}`}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
          {!showAllVideos && videos.length >= 4 && (
            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAllVideos(true);
                  loadVideos(true);
                }}
                data-testid={`view-all-${category}-videos`}
              >
                View all videos
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Video preview modal */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{preview?.title}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="aspect-video w-full">
              <iframe
                src={getYoutubeEmbed(preview.youtube_url)}
                title={preview.title}
                className="w-full h-full rounded-md"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
